import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getRouteStackPath } from '@portal/config/routes';
import CreatePixKey from '@portal/components/CreatePixKey/CreatePixKey';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import { encrypt } from '@portal/repositories/securitySdkConfig';
import { PixStatus, PixType } from '@portal/enum/pix';
import { PixActions } from '@portal/actions';
import { translate } from '@portal/services/i18n';
import AlertIcon from '@portal/assets/svg/upload-modal/ic_alert.svg?react';
import EnvelopeIcon from '@portal/assets/svg/pix-buttons/new-key/ic_envelope.svg?react';
import CellphoneIcon from '@portal/assets/svg/pix-buttons/new-key/ic_cellphone.svg?react';
import TaxIdentifierIcon from '@portal/assets/svg/pix-buttons/new-key/ic_tax-identifier.svg?react';
import TokenIcon from '@portal/assets/svg/pix-buttons/new-key/ic_token.svg?react';
import {
  unmaskField,
  formatPixKey,
  maskCpfOrCnpjHide,
  maskCpf,
  maskCnpj,
  maskPhoneInput,
} from '@portal/services/masks';
import ConfirmationPix from '@portal/components/ConfirmationPix/ConfirmationPix';
import { useLocation } from 'react-router';

import { validateEmail } from '@portal/utils/validate';
import { getPixKeyType } from '@portal/utils/pixKeyType';

import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import AdvancedSteps from '@portal/components/AdvancedSteps/AdvancedSteps';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { ArrowRightOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import { usePixPortability } from '@portal/hooks/usePixPortability';
import PixApi from '@portal/repositories/pix';
import MessageService from '@portal/services/message';
import AdvancedModal from '@portal/components/AdvancedModal/AdvancedModal';

import { decreaseLoading, increaseLoading } from '@portal/actions/loading';
import { ErrorsMessages } from '@portal/enum/errors';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';

enum TabIndexEnum {
  SELECT_TYPE = '1',
  CREATE_KEY = '2',
  KEY_EXISTS_ELSEWHERE = '3',
  CONFIRMATION = '4',
}

const NewKeyPix = () => {
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);

  const {
    theme: { currentTheme },
    pix: { pixKeys },
  } = useReduxState();

  const effectivePixType = (): number | null => {
    if (pixType >= PixType.TAX_IDENTIFIER && pixType <= PixType.RANDOM) {
      return pixType;
    }
    return getPixKeyType(newPixKey);
  };

  const isButtonDisabled = () => {
    const effective = effectivePixType();
    if (effective === null) return true;
    if (effective === PixType.RANDOM) return false;
    if (pixKeyFormHasError) return true;
    if (effective === PixType.PHONE) return !isPhoneValid;
    if (effective === PixType.EMAIL) return !isEmailValid;
    return false;
  };
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const location: any = useLocation();

  const dispatch = useDispatch();
  const navigate = useNavigation();
  const {
    confirmPortabilityWithPin,
    requestPortabilityCodeWithPin,
    isPortabilityEnabled,
  } = usePixPortability();
  const pixDetails = useSelector(
    (state: reducers.rootReducer) => state.pix.registerDetails
  );
  const verifyCodeSuccess = useSelector(
    (state: reducers.rootReducer) => state.pix.verifyCodeSuccess
  );
  const me = useSelector((state: reducers.rootReducer) => state.user.me);
  const ispb = useSelector((state: reducers.rootReducer) => state.user.ispb);

  const [tabKey, setTabKey] = useState('1');
  const [pixType, setPixType] = useState<number>(-1);
  const [pixKeyFormHasError, setPixKeyFormHasError] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openPortabilityModal, setOpenPortabilityModal] = useState(false);
  const [openRequestPortabilityPinModal, setOpenRequestPortabilityPinModal] =
    useState(false);
  const [, setConfirmationKey] = useState<string | null>(null);
  const [, setConfirmationProtocolId] = useState<string | null>(null);
  const [newPixKey, setNewPixKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [pixInfoFromApi, setPixInfoFromApi] = useState<bank.PixInfo | null>(
    null
  );
  const [alertModal, setAlertModal] = useState<{
    title: string;
    message: string;
  } | null>(null);

  const handleSetNewPixKey = (value: string) => {
    setNewPixKey(value);
    if (pixType === PixType.RANDOM) setPixType(-1);
  };

  const addCountryCode = (phoneNumber: string) => {
    const countryCode = '+55';
    const unmaskedPhoneNumber = unmaskField(phoneNumber);
    return countryCode.concat(unmaskedPhoneNumber);
  };

  const registerKey = async (pin?: string) => {
    const effectiveType =
      pixType === PixType.RANDOM
        ? PixType.RANDOM
        : getPixKeyType(newPixKey) ?? pixType;
    const requestData: models.RegisterPixKey = {
      key: newPixKey,
      pixType: effectiveType,
    };

    if (pin) {
      const encryptedPin = await encrypt(pin);
      requestData.authenticate = { pin: encryptedPin };
    }

    if (effectiveType === PixType.PHONE) {
      requestData.key = addCountryCode(newPixKey);
    }

    dispatch(PixActions.registerPixKey(requestData));
  };

  const confirmPortability = async (pin: string) => {
    const effectiveType = pixDetails?.type ?? effectivePixType() ?? pixType;
    const keyForRequest =
      pixDetails?.key ??
      (effectiveType === PixType.PHONE ? addCountryCode(newPixKey) : newPixKey);
    await confirmPortabilityWithPin(
      pin,
      keyForRequest,
      effectiveType,
      verificationCode,
      !!pixInfoFromApi
    );
  };

  const requestPortabilityCodeWithoutPin = async () => {
    if (!isPortabilityEnabled) {
      MessageService.error('PORTABILITY_DISABLED');
      return;
    }

    const effectiveType = effectivePixType() ?? pixType;
    const keyForRequest =
      effectiveType === PixType.PHONE
        ? addCountryCode(newPixKey)
        : effectiveType === PixType.TAX_IDENTIFIER
        ? unmaskField(newPixKey)
        : newPixKey;
    try {
      dispatch(increaseLoading());
      const requestData: models.RegisterPixKey = {
        key: keyForRequest,
        pixType: effectiveType,
        createReivindication: true,
      };
      const result = await dispatch(
        PixActions.requestPortabilityCode(requestData)
      );
      const payload = result as unknown as
        | models.RegisterPixKeyDetails
        | undefined;

      if (payload?.key) setConfirmationKey(payload.key);
      if (payload?.protocolId) setConfirmationProtocolId(payload.protocolId);
      setTabKey(TabIndexEnum.CONFIRMATION);
    } catch {
      setPixInfoFromApi(null);
      setTabKey(TabIndexEnum.CREATE_KEY);
    } finally {
      dispatch(decreaseLoading());
    }
  };

  const getKeyForRequest = () => {
    if (effectivePixType() === PixType.EMAIL) {
      return newPixKey;
    }

    if (effectivePixType() === PixType.PHONE) {
      return addCountryCode(newPixKey);
    }

    return unmaskField(newPixKey);
  };

  const advanceKeyTab = async () => {
    if (pixType === PixType.RANDOM) {
      setOpenModal(true);
      return;
    }

    try {
      dispatch(increaseLoading());
      const keyForRequest = getKeyForRequest();

      const data = await PixApi.getPixInfo(keyForRequest);
      const myTaxId = unmaskField(
        me?.account?.taxIdentifier ||
          me?.account?.representants?.find((r) => r.main)?.taxIdentifier ||
          ''
      );
      const pixTaxId = unmaskField(data.taxIdentifier ?? '');
      const sameTaxId =
        myTaxId.length > 0 && pixTaxId.length > 0 && myTaxId === pixTaxId;
      const sameBank =
        ispb != null &&
        data.ispb != null &&
        String(ispb).trim() === String(data.ispb).trim();

      if (sameTaxId && sameBank) {
        setAlertModal({
          title: translate('PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.TITLE'),
          message: translate(
            'PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.KEY_ALREADY_EXISTS_HERE'
          ),
        });
        return;
      }

      if (!sameTaxId && effectivePixType() !== PixType.PHONE) {
        setAlertModal({
          title: translate('PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.TITLE'),
          message: translate(
            'PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.PORTABILITY_ONLY_PHONE'
          ),
        });
        return;
      }

      setPixInfoFromApi(data);
      setTabKey(TabIndexEnum.KEY_EXISTS_ELSEWHERE);
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === ErrorsMessages.PIX_KEY_NOT_FOUND) {
          if (pixType === PixType.TAX_IDENTIFIER) {
            setOpenModal(true);
          } else {
            registerKey();
          }
          return;
        }

        MessageService.error(error.message);
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

  const handleVerificationCode = (value: string) => {
    setVerificationCode(value);
  };

  const handleConfirmClick = () => {
    if (verificationCode.length !== 6) return;
    setOpenPortabilityModal(true);
  };

  const handleResendCode = () => {
    if (pixInfoFromApi) {
      requestPortabilityCodeWithoutPin();
      return;
    }
    registerKey();
  };

  useEffect(() => {
    if (location && location.state && location.state.pixKey) {
      setTabKey(TabIndexEnum.KEY_EXISTS_ELSEWHERE);
    }
    dispatch(PixActions.cleanRegisterPixKey());
  }, [dispatch, location]);

  useEffect(() => {
    if (pixDetails && tabKey !== TabIndexEnum.SELECT_TYPE) {
      if (pixType === PixType.RANDOM || pixType === PixType.TAX_IDENTIFIER) {
        setOpenModal(false);
        navigate(getRouteStackPath('PIX', 'PIX'));

        return;
      }

      setOpenModal(false);
      setTabKey(TabIndexEnum.CONFIRMATION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixDetails, tabKey]);

  useEffect(() => {
    if (verifyCodeSuccess && tabKey !== TabIndexEnum.SELECT_TYPE) {
      navigate(getRouteStackPath('PIX', 'PIX'));
    }
  }, [navigate, verifyCodeSuccess, tabKey]);

  useEffect(() => {
    const phoneValid = newPixKey.replace(/\D+/g, '').length === 11;
    const emailValid = validateEmail(newPixKey);
    setIsPhoneValid(phoneValid);
    setIsEmailValid(emailValid);
  }, [newPixKey]);

  const handlePortabilityRequest = () => {
    if (!isPortabilityEnabled) {
      MessageService.error('PORTABILITY_DISABLED');
      return;
    }
    setOpenRequestPortabilityPinModal(true);
  };

  const confirmRequestPortabilityWithPin = async (pin: string) => {
    const effectiveType = effectivePixType() ?? pixType;
    const keyForRequest =
      effectiveType === PixType.PHONE
        ? addCountryCode(newPixKey)
        : effectiveType === PixType.TAX_IDENTIFIER
        ? unmaskField(newPixKey)
        : newPixKey;
    try {
      const payload = await requestPortabilityCodeWithPin(
        pin,
        keyForRequest,
        effectiveType
      );
      if (payload?.key) setConfirmationKey(payload.key);
      if (payload?.protocolId) setConfirmationProtocolId(payload.protocolId);
      setTabKey(TabIndexEnum.CONFIRMATION);
    } catch {
      setPixInfoFromApi(null);
      setTabKey(TabIndexEnum.CREATE_KEY);
    }
  };

  const iconColor = currentTheme?.primaryColor ?? currentTheme?.background;

  const ACTIVE_PIX_STATUSES = [
    PixStatus.CLEARING_REGISTRATION_PENDING,
    PixStatus.ACTIVE,
  ];
  const hasTaxIdentifierKey = (pixKeys ?? []).some(
    (k) =>
      k.type === PixType.TAX_IDENTIFIER &&
      ACTIVE_PIX_STATUSES.includes(k.status)
  );
  const hasEmailKey = (pixKeys ?? []).some(
    (k) => k.type === PixType.EMAIL && ACTIVE_PIX_STATUSES.includes(k.status)
  );
  const hasPhoneKey = (pixKeys ?? []).some(
    (k) => k.type === PixType.PHONE && ACTIVE_PIX_STATUSES.includes(k.status)
  );

  const keyTypeActions = [
    {
      title: translate('PAGES.HOME.PIX.PIX_TYPES.EMAIL'),
      text: translate('PAGES.HOME.PIX.TEXT.EMAIL'),
      icon: <EnvelopeIcon fill={iconColor} width={24} height={24} />,
      onClick: () => {
        setNewPixKey(hasEmailKey ? '' : me?.email ?? '');
        setPixType(PixType.EMAIL);
        setTabKey(TabIndexEnum.CREATE_KEY);
      },
      key: 'email',
      disabled: false,
    },
    {
      title: translate('PAGES.HOME.PIX.PIX_TYPES.PHONE'),
      text: translate('PAGES.HOME.PIX.TEXT.PHONE'),
      icon: <CellphoneIcon fill={iconColor} width={24} height={24} />,
      onClick: () => {
        if (!hasPhoneKey) {
          const digits = (me?.cellphone ?? '').replace(/\D/g, '');
          const national =
            digits.startsWith('55') && digits.length >= 12
              ? digits.slice(2)
              : digits;
          setNewPixKey(national ? maskPhoneInput(national) : '');
        } else {
          setNewPixKey('');
        }
        setPixType(PixType.PHONE);
        setTabKey(TabIndexEnum.CREATE_KEY);
      },
      key: 'phone',
      disabled: false,
    },
    {
      title: translate('PAGES.HOME.PIX.PIX_TYPES.TAX_ID'),
      text: hasTaxIdentifierKey
        ? translate('PAGES.HOME.PIX.TEXT.TAX_ID_ALREADY_REGISTERED')
        : translate('PAGES.HOME.PIX.TEXT.TAX_ID'),
      icon: <TaxIdentifierIcon fill={iconColor} width={24} height={24} />,
      onClick: hasTaxIdentifierKey
        ? undefined
        : () => {
            const taxId =
              me?.account?.taxIdentifier ??
              (me?.account?.type === AccountTaxIdentifierType.CORPORATE
                ? me?.account?.representants?.find((r) => r.main)?.taxIdentifier
                : undefined) ??
              '';
            const digits = unmaskField(taxId);
            const filled =
              digits.length === 11
                ? maskCpf(digits)
                : digits.length === 14
                ? maskCnpj(digits)
                : '';
            setNewPixKey(filled);
            setPixType(PixType.TAX_IDENTIFIER);
            setTabKey(TabIndexEnum.CREATE_KEY);
          },
      key: 'taxIdentifier',
      disabled: hasTaxIdentifierKey,
    },
    {
      title: translate('PAGES.HOME.PIX.PIX_TYPES.RANDOM'),
      text: translate('PAGES.HOME.PIX.TEXT.TOKEN'),
      icon: <TokenIcon fill={iconColor} width={24} height={24} />,
      onClick: () => {
        setNewPixKey('');
        setPixType(PixType.RANDOM);
        setTabKey(TabIndexEnum.CREATE_KEY);
      },
      key: 'random',
      disabled: false,
    },
  ];

  const selectTypeStep = {
    value: TabIndexEnum.SELECT_TYPE,
    label: translate('PAGES.HOME.PIX.TABS.KEY_TYPE'),
    children: (
      <div className="new-key-pix__select-type">
        <ThemedText variant="h2" className="new-key-pix__select-type__title">
          {translate('PAGES.HOME.PIX.TABS.SELECT_KEY_TYPE_TITLE')}
        </ThemedText>
        <div className="new-key-pix__grid-actions">
          {keyTypeActions.map((action) => (
            <button
              type="button"
              key={action.key}
              className={`new-key-pix__grid-actions__card${
                action.disabled
                  ? ' new-key-pix__grid-actions__card--disabled'
                  : ''
              }`}
              onClick={action.disabled ? undefined : action.onClick}
              disabled={action.disabled}
              data-testid={`new-pix-key-action-${action.key}`}
            >
              <div className="new-key-pix__grid-actions__card-content">
                <div className="new-key-pix__grid-actions__card-icon">
                  {action.icon}
                </div>
                <div className="new-key-pix__grid-actions__card-text">
                  <ThemedText
                    variant="h3"
                    className="new-key-pix__grid-actions__title"
                  >
                    {action.title}
                  </ThemedText>
                  <ThemedText>{action.text}</ThemedText>
                  {action.disabled ? (
                    <span className="new-key-pix__grid-actions__card-badge">
                      <CheckCircleOutlined />
                      {translate('PAGES.HOME.PIX.TEXT.KEY_ALREADY_LINKED')}
                    </span>
                  ) : null}
                </div>
              </div>
              {!action.disabled && (
                <ArrowRightOutlined className="new-key-pix__grid-actions__card-chevron" />
              )}
            </button>
          ))}
        </div>
      </div>
    ),
  };

  const confirmationStep = {
    value: TabIndexEnum.CONFIRMATION,
    label: translate('PAGES.HOME.PIX.TABS.CONFIRMATION'),
    children: (
      <div className="new-key-pix__step-content">
        <ConfirmationPix
          newPixKey={newPixKey}
          setVerificationCode={handleVerificationCode}
          handleResendCode={handleResendCode}
          pixType={effectivePixType() ?? pixType}
        />
        <div className="new-key-pix__grid-actions__button">
          <AdvancedButton
            text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
            variant="outlined"
            onClick={() => {
              dispatch(PixActions.cleanRegisterPixKey());
              if (pixInfoFromApi) {
                setTabKey(TabIndexEnum.KEY_EXISTS_ELSEWHERE);
                return;
              }

              setTabKey(TabIndexEnum.CREATE_KEY);
            }}
          />
          <AdvancedButton
            text={translate('PAGES.HOME.PIX.TABS.CONFIRM_BUTTON')}
            onClick={handleConfirmClick}
            disabled={verificationCode.length !== 6}
          />
        </div>
      </div>
    ),
  };

  const keyExistsElsewhereStep = pixInfoFromApi
    ? {
        value: TabIndexEnum.KEY_EXISTS_ELSEWHERE,
        label: translate('PAGES.HOME.PIX.TABS.KEY_EXISTS_ELSEWHERE'),
        children: (
          <div className="new-key-pix__step-content">
            <div className="new-key-pix__key-exists-card">
              <div className="new-key-pix__key-exists-card__header">
                <AlertIcon width={48} height={48} />
                <h3 className="new-key-pix__key-exists-card__title">
                  {translate(
                    'PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.CONFIRMATION_TITLE'
                  )}
                </h3>
                <p className="new-key-pix__key-exists-card__message">
                  {translate('PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.MESSAGE', {
                    bank: pixInfoFromApi.bank ?? '',
                  })}
                </p>
              </div>
              <div className="new-key-pix__key-exists-card__details">
                <h4 className="new-key-pix__key-exists-card__details-title">
                  {translate(
                    'PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.DETAILS_TITLE'
                  )}
                </h4>
                <dl className="new-key-pix__key-exists-card__grid">
                  <dt>
                    {translate('PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.KEY_LABEL')}
                  </dt>
                  <dd>
                    {formatPixKey(pixInfoFromApi.key) ?? pixInfoFromApi.key}
                  </dd>
                  {pixInfoFromApi.name ? (
                    <>
                      <dt>
                        {translate(
                          'PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.NAME_LABEL'
                        )}
                      </dt>
                      <dd>{pixInfoFromApi.name}</dd>
                    </>
                  ) : null}
                  {pixInfoFromApi.taxIdentifier ? (
                    <>
                      <dt>
                        {translate(
                          'PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.TAX_ID_LABEL'
                        )}
                      </dt>
                      <dd>{maskCpfOrCnpjHide(pixInfoFromApi.taxIdentifier)}</dd>
                    </>
                  ) : null}
                  {pixInfoFromApi.bank ? (
                    <>
                      <dt>
                        {translate(
                          'PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.BANK_LABEL'
                        )}
                      </dt>
                      <dd>{pixInfoFromApi.bank}</dd>
                    </>
                  ) : null}
                  {pixInfoFromApi.branch ? (
                    <>
                      <dt>
                        {translate(
                          'PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.BRANCH_LABEL'
                        )}
                      </dt>
                      <dd>{pixInfoFromApi.branch}</dd>
                    </>
                  ) : null}
                  {pixInfoFromApi.account ||
                  (pixInfoFromApi as { accountDigit?: string })
                    ?.accountDigit ? (
                    <>
                      <dt>
                        {translate(
                          'PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.ACCOUNT_LABEL'
                        )}
                      </dt>
                      <dd>
                        {[
                          pixInfoFromApi.account,
                          (pixInfoFromApi as { accountDigit?: string })
                            ?.accountDigit,
                        ]
                          .filter(Boolean)
                          .join('')}
                      </dd>
                    </>
                  ) : null}
                </dl>
              </div>
            </div>
            <div className="new-key-pix__grid-actions__button">
              <AdvancedButton
                text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
                variant="outlined"
                onClick={() => {
                  setPixInfoFromApi(null);
                  setTabKey(TabIndexEnum.CREATE_KEY);
                }}
              />

              <AdvancedButton
                text={translate(
                  'PAGES.HOME.PIX.KEY_EXISTS_ELSEWHERE.DO_PORTABILITY_BUTTON'
                )}
                onClick={handlePortabilityRequest}
              />
            </div>
          </div>
        ),
      }
    : null;

  const stepOptions = [
    selectTypeStep,
    {
      value: TabIndexEnum.CREATE_KEY,
      label: translate('PAGES.HOME.PIX.TABS.KEY'),
      children: (
        <div className="new-key-pix__step-content">
          <CreatePixKey
            pixType={pixType}
            accountType={me?.account?.type ?? undefined}
            setNewPixKey={handleSetNewPixKey}
            newPixKey={newPixKey}
            onValidationChange={setPixKeyFormHasError}
            onSubmit={() => {
              if (!isButtonDisabled()) {
                advanceKeyTab();
              }
            }}
          />
          <div className="new-key-pix__grid-actions__button">
            <AdvancedButton
              text={translate('COMPONENTS.ADVANCED_BUTTON.RETURN')}
              variant="outlined"
              onClick={() => {
                setPixType(-1);
                setTabKey(TabIndexEnum.SELECT_TYPE);
              }}
            />
            <AdvancedButton
              text={translate('COMPONENTS.ADVANCED_BUTTON.ADVANCE')}
              onClick={advanceKeyTab}
              disabled={isButtonDisabled()}
            />
          </div>
        </div>
      ),
    },
    ...(keyExistsElsewhereStep ? [keyExistsElsewhereStep] : []),
    confirmationStep,
  ];

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.PIX'),
            url: getRouteStackPath('PIX', 'PIX'),
          },
          {
            active: false,
            title: translate('COMPONENTS.BREADCRUMB.NEW_KEY'),
            url: getRouteStackPath('PIX', 'KEYS'),
          },
        ]}
      />
      <div className="new-key-pix">
        <AdvancedPanelContentHeader
          testId="new-pix-header"
          title={translate('COMPONENTS.BREADCRUMB.NEW_KEY')}
          description={translate('PAGES.HOME.PIX.DESCRIPTIONS.CREATE_KEY')}
          goBackAction={() => navigate(getRouteStackPath('PIX', 'PIX'))}
          buttonTitle={translate('PAGES.HOME.PIX.BUTTONS.BACK_PIX')}
        />
        <AdvancedSteps
          testId="advanced-steps-pix-create-key"
          steps={stepOptions}
          currentStep={tabKey}
          onStepChange={(step) => setTabKey(String(step))}
        />
      </div>
      <ConfirmationModal
        visible={openModal}
        setVisible={setOpenModal}
        handleConfirmation={registerKey}
      />
      <ConfirmationModal
        visible={openPortabilityModal}
        setVisible={setOpenPortabilityModal}
        handleConfirmation={(pin) => {
          confirmPortability(pin);
          setOpenPortabilityModal(false);
        }}
      />
      <ConfirmationModal
        visible={openRequestPortabilityPinModal}
        setVisible={setOpenRequestPortabilityPinModal}
        handleConfirmation={(pin) => {
          confirmRequestPortabilityWithPin(pin);
          setOpenRequestPortabilityPinModal(false);
        }}
      />
      <AdvancedModal
        visible={alertModal != null}
        title={alertModal?.title ?? ''}
        onClose={() => setAlertModal(null)}
        actions={[
          {
            label: translate('COMPONENTS.ADVANCED_BUTTON.OK'),
            onClick: () => setAlertModal(null),
            type: 'ok',
          },
        ]}
        size="small"
        autoHeight
      >
        {alertModal != null ? <p>{alertModal.message}</p> : null}
      </AdvancedModal>
    </>
  );
};

export default NewKeyPix;
